import {
  IntlProvider,
  load,
  loadMessages,
  LocalizationProvider,
} from "@progress/kendo-react-intl";
import currencyData from "cldr-core/supplemental/currencyData.json";
import likelySubtags from "cldr-core/supplemental/likelySubtags.json";
import weekData from "cldr-core/supplemental/weekData.json";
import {
  ReactNode,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import LanguageContext from "../../libs/context/LanguageContext";
import kendoDatePickerMessages from "../../messages/kendo/datePicker";
import kendoDropdownMessages from "../../messages/kendo/dropdowns";
import kendoGridMessages from "../../messages/kendo/grid";

// eslint-disable-next-line @typescript-eslint/naming-convention
const IntlProviderForKendoControls = (props: {
  messages: Record<string, string>;
  children: Array<ReactNode> | ReactNode;
}): JSX.Element => {
  const [currentLocale, setCurrentLocale] = useState("en");
  const { locale } = useContext(LanguageContext);
  const kendoLocale = getLocaleToUseForKendoControls(locale);
  // load the locale data dynamically when the locale changes
  useEffect(() => {
    if (kendoLocale) {
      Promise.all([
        import("cldr-dates-full/main/" + kendoLocale + "/ca-gregorian.json"),
        import("cldr-dates-full/main/" + kendoLocale + "/dateFields.json"),
        import("cldr-dates-full/main/" + kendoLocale + "/timeZoneNames.json"),
        import("cldr-numbers-full/main/" + kendoLocale + "/numbers.json"),
      ]).then(([caGregorian, dateFields, timeZoneNames, numbers]) => {
        load(
          likelySubtags,
          currencyData,
          weekData,
          numbers.default,
          caGregorian.default,
          dateFields.default,
          timeZoneNames.default
        );
        setCurrentLocale(kendoLocale);
      });
    }
  }, [kendoLocale]);

  // Load Kendo localized messages when props.messages changes — that prop is refreshed each
  // time localisation texts are fetched from the server. Pass kendoLocale (not raw locale from
  // LanguageContext) so loadMessages uses the same normalized key as currentLocale after CLDR
  // load (e.g. DB "es-us" and context "es-US" both resolve to "es-US").
  useEffect(() => {
    if (props.messages) {
      loadKendoLocalizedMessages(props.messages, kendoLocale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.messages]);

  // return the IntlProvider with the current locale, memoize the component to avoid re-rendering while the locale is changing
  // otherwise, the kendo controls will throw an error saying that the locale is not loaded
  return useMemo(() => {
    return (
      <>
        <Suspense fallback={<></>}>
          <IntlProvider locale={currentLocale}>
            <LocalizationProvider language={currentLocale}>
              {/* render the childern nodes */}
              {props.children}
            </LocalizationProvider>
          </IntlProvider>
        </Suspense>
      </>
    );
  }, [props.children, currentLocale]);
};

/**
 * Get the locale to use for Kendo controls. If two characters, e.g "en", then return it as is otherwise convert it to "en-US" format.
 * @param locale  Current site locale.
 * @returns  The locale to use for Kendo controls.
 */
export const getLocaleToUseForKendoControls = (locale: string): string => {
  // Normalize the locale string for Kendo controls
  // If the locale contains a hyphen (e.g., "en-us"), convert it to "en-US" format
  // Kendo requires the region part (after the hyphen) to be uppercase
  if (locale.includes("-")) {
    const [lang, region] = locale.split("-"); // Split into language and region
    locale = `${lang}-${region.toUpperCase()}`; // Reconstruct with uppercase region
  }

  // Return the normalized locale string
  return locale;
};

interface DynamicObject {
  [key: string]: any;
}

/**
 * Loads localized messages for Kendo UI components into the Kendo localization system.
 * This function takes the default messages defined for Kendo components, looks up their translations
 * in the provided data object, and registers them with Kendo's localization system.
 *
 * @param data - Object containing key-value pairs of translated messages where key is the message identifier
 * @param language - The language code to register these messages for (e.g., 'en-US', 'es-ES')
 *
 * @example
 * loadKendoLocalizedMessages(
 *   translations,
 *   'en-US'
 * );
 */
function loadKendoLocalizedMessages(
  data: Record<string, string>,
  language: string
) {
  if (data && Object.keys(data).length > 0) {
    // Define configurations for Kendo components
    const kendoComponentConfigs = [
      { messages: kendoGridMessages, component: "KendoGrid", type: "grid" },
      {
        messages: kendoDropdownMessages,
        component: "KendoDropdowns",
        type: "dropdowns",
      },
      {
        messages: kendoDatePickerMessages,
        component: "KendoDatePicker",
        type: "multiviewcalendar",
      },
      {
        messages: kendoDatePickerMessages,
        component: "KendoDatePicker",
        type: "dateinput",
      },
      {
        messages: kendoDatePickerMessages,
        component: "KendoDatePicker",
        type: "calendar",
      },
      {
        messages: kendoDatePickerMessages,
        component: "KendoDatePicker",
        type: "datepicker",
      },
      {
        messages: kendoDatePickerMessages,
        component: "KendoDatePicker",
        type: "daterangepicker",
      },
      {
        messages: kendoDatePickerMessages,
        component: "KendoDatePicker",
        type: "timepicker",
      },
      {
        messages: kendoDatePickerMessages,
        component: "KendoDatePicker",
        type: "datetimepicker",
      },
    ];

    // Load localized messages for all Kendo components
    kendoComponentConfigs.forEach((config) => {
      loadKendoLocalizedMessagesByComponent(
        data,
        language,
        config.messages,
        config.component,
        config.type
      );
    });
  }
}

/**
 * Loads localized messages for a specific Kendo UI component into the Kendo localization system.
 * This function takes the default messages defined for a Kendo component, looks up their translations
 * in the provided data object, and registers them with Kendo's localization system.
 *
 * @param data - Object containing key-value pairs of translated messages where key is the message identifier
 * @param language - The language code to register these messages for (e.g., 'en-US', 'es-ES')
 * @param messages - The default messages object for the Kendo component (from defineMessages)
 * @param kendoResourceSetIdentifier - The prefix used to identify messages for this component (e.g., 'KendoGrid', 'KendoDatePicker')
 * @param kendoLocalizationPropName - The property name under which Kendo expects these messages (e.g., 'grid', 'datepicker')
 *
 * @example
 * loadKendoLocalizedMessagesByComponent(
 *   translations,
 *   'en-US',
 *   gridMessages,
 *   'KendoGrid',
 *   'grid'
 * );
 */
function loadKendoLocalizedMessagesByComponent(
  data: Record<string, string>,
  language: string,
  messages: any,
  kendoResourceSetIdentifier: string,
  kendoLocalizationPropName: string
) {
  // Create an object to store the localized messages
  const localizedMessages: DynamicObject = {};

  // Iterate through each message key in the default messages
  Object.keys(messages).forEach((key) => {
    const msg = (messages as DynamicObject)[key];
    // Look up the translation using the full identifier, fallback to default message if not found
    localizedMessages[key] =
      data[kendoResourceSetIdentifier + "." + key] ?? msg.defaultMessage;
  });

  // Create the final object structure that Kendo expects
  const localizedMessagesObj: DynamicObject = {};
  localizedMessagesObj[kendoLocalizationPropName] = localizedMessages;
  // Register the localized messages with Kendo's localization system
  loadMessages(localizedMessagesObj, language);
}
export default IntlProviderForKendoControls;
